import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

const STRUCTURED_DATA_ID = 'chainex-structured-data';

export interface PageStructuredData {
  canonicalUrl: string;
  title: string;
  description?: string;
}

export interface StructuredDataSite {
  origin: string;
  name: string;
  isChainEX: boolean;
  socialProfiles?: string[];
}

export function buildStructuredData(
  page: PageStructuredData,
  site: StructuredDataSite = {
    origin: new URL(environment.config.APP_URL).origin,
    name: environment.config.EXCHANGE_NAME,
    isChainEX: environment.config.EXCHANGE_NAME_L === 'chainex',
    socialProfiles: [
      environment.config.FACEBOOK_LINK,
      environment.config.TWITTER_LINK,
      environment.config.LINKEDIN_LINK,
      environment.config.INSTAGRAM_LINK,
      environment.config.YOUTUBE_LINK,
    ].filter((profile): profile is string => Boolean(profile)),
  },
): Record<string, unknown> | null {
  let canonical: URL;
  try {
    canonical = new URL(page.canonicalUrl);
  } catch {
    return null;
  }

  if (canonical.protocol !== 'https:' || canonical.origin !== site.origin || !page.title.trim()) {
    return null;
  }

  const organization: Record<string, unknown> = {
    '@type': 'Organization',
    '@id': `${site.origin}/#organization`,
    name: site.name,
    url: `${site.origin}/`,
  };

  if (site.isChainEX) {
    organization.legalName = 'ChainEX (Pty) Ltd';
    organization.logo = `${site.origin}/assets/chainex/logo_orange.svg`;
    organization.areaServed = {
      '@type': 'Country',
      name: 'South Africa',
    };
    organization.identifier = {
      '@type': 'PropertyValue',
      name: 'Financial Services Provider number',
      value: '53799',
    };
    if (site.socialProfiles?.length) {
      organization.sameAs = site.socialProfiles;
    }
  }

  const websiteId = `${site.origin}/#website`;

  const webPage: Record<string, unknown> = {
    '@type': 'WebPage',
    '@id': `${canonical.href}#webpage`,
    url: canonical.href,
    name: page.title.trim(),
    isPartOf: { '@id': websiteId },
    about: { '@id': `${site.origin}/#organization` },
  };
  if (page.description?.trim()) {
    webPage.description = page.description.trim();
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      organization,
      {
        '@type': 'WebSite',
        '@id': websiteId,
        url: `${site.origin}/`,
        name: site.name,
        publisher: { '@id': `${site.origin}/#organization` },
      },
      webPage,
    ],
  };
}

export function serializeStructuredData(data: Record<string, unknown>): string {
  // JSON-LD can contain route metadata sourced from a remote asset. Escaping '<'
  // prevents a string such as '</script>' from closing the script element.
  return JSON.stringify(data).replace(/</g, '\\u003c');
}

export function updateStructuredDataScript(
  document: Document,
  page: PageStructuredData,
  site?: StructuredDataSite,
): void {
  const data = buildStructuredData(page, site);
  let script = document.getElementById(STRUCTURED_DATA_ID) as HTMLScriptElement | null;
  if (!data) {
    script?.remove();
    return;
  }
  if (!script) {
    script = document.createElement('script');
    script.id = STRUCTURED_DATA_ID;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = serializeStructuredData(data);
}

@Injectable({ providedIn: 'root' })
export class StructuredDataService {
  private observer: MutationObserver | null = null;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  updatePage(): void {
    this.renderPageData();
    this.observeMetadataChanges();
  }

  private renderPageData(): void {
    const canonicalUrl = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href;
    const description = this.document.querySelector<HTMLMetaElement>('meta[name="description"]')?.content;
    if (canonicalUrl) {
      updateStructuredDataScript(this.document, {
        canonicalUrl,
        title: this.document.title,
        description,
      });
    } else {
      this.document.getElementById(STRUCTURED_DATA_ID)?.remove();
    }
  }

  private observeMetadataChanges(): void {
    const MutationObserverConstructor = this.document.defaultView?.MutationObserver;
    if (this.observer || !MutationObserverConstructor) {
      return;
    }

    this.observer = new MutationObserverConstructor(records => {
      if (records.some(record => this.isPageMetadataMutation(record))) {
        this.renderPageData();
      }
    });
    this.observer.observe(this.document.head, {
      attributes: true,
      attributeFilter: ['content', 'href', 'rel'],
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  private isPageMetadataMutation(record: MutationRecord): boolean {
    const ElementConstructor = this.document.defaultView?.Element;
    const target = ElementConstructor && record.target instanceof ElementConstructor
      ? record.target as Element
      : record.target.parentElement;
    if (!target || target.closest(`#${STRUCTURED_DATA_ID}`)) {
      return false;
    }
    const window = this.document.defaultView;
    if ((window?.HTMLTitleElement && target instanceof window.HTMLTitleElement) ||
      (window?.HTMLMetaElement && target instanceof window.HTMLMetaElement) ||
      (window?.HTMLLinkElement && target instanceof window.HTMLLinkElement)) {
      return true;
    }
    return [...record.addedNodes, ...record.removedNodes].some(node => {
      if (!ElementConstructor || !(node instanceof ElementConstructor)) {
        return false;
      }
      return node.matches('title, meta[name="description"], link[rel="canonical"]') ||
        Boolean(node.querySelector('title, meta[name="description"], link[rel="canonical"]'));
    });
  }
}
